import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import LikeIcon from '@atlaskit/icon/glyph/like';
import { B400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { VOTES_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	getLabelForToggleWithoutSelfVote,
	getLabelForToggleWithSelfVote,
} from '../../common/utils.tsx';
import messages from '../../messages.tsx';
import { useVoteField } from '../../services/vote-field-service/index.tsx';
import VoteButton from '../button/index.tsx';
import type { Props } from './types.tsx';

export const VoteToggle = ({
	onClick,
	isDropdownItem = false,
	disableVotingProps = { isDisabled: false },
	onFieldUpdated,
	useVoteField: useVoteFieldFromProps,
	...rest
}: Props) => {
	const { isDisabled, tooltips } = disableVotingProps;
	const { formatMessage } = useIntl();
	const [{ value }, { toggleValue }] = (
		fg('relay-migration-issue-header-and-parent')
			? useVoteFieldFromProps || useVoteField
			: useVoteField
	)({ onFieldUpdated });

	const { buttonLabel, disabledTooltip, ariaLabel } = useMemo(
		() =>
			value.hasVoted
				? {
						buttonLabel: formatMessage(messages.removeVoteLabel),
						disabledTooltip: tooltips?.removeVote,
						ariaLabel: formatMessage(getLabelForToggleWithSelfVote(), {
							votes: value.votes,
						}),
					}
				: {
						buttonLabel: formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.addVoteLabelIssueTermRefresh
								: messages.addVoteLabel,
						),
						disabledTooltip: tooltips?.addVote,
						ariaLabel: formatMessage(getLabelForToggleWithoutSelfVote(value.votes), {
							votes: value.votes,
						}),
					},
		[value.hasVoted, value.votes, formatMessage, tooltips?.removeVote, tooltips?.addVote],
	);

	const toggleVote = useCallback(() => {
		// remove check for createAnalyticsEvent when cleaning up one_event_rules_them_all_fg
		if (fg('one_event_rules_them_all_fg')) {
			getUpdateAnalyticsFlowHelper().setAttributes(VOTES_TYPE, {
				actionTaken: value.hasVoted ? 'remove' : 'add',
			});
		}
		onClick?.();
		toggleValue(isDropdownItem);
	}, [isDropdownItem, onClick, toggleValue, value.hasVoted]);

	if (isDropdownItem) {
		const itemAriaLabel = {
			'aria-label': !isDisabled || disabledTooltip === undefined ? undefined : disabledTooltip,
		};

		return (
			<div role="menu">
				<Tooltip
					testId="issue-field-voters.ui.vote-toggle.tooltip"
					content={!isDisabled || disabledTooltip === undefined ? null : disabledTooltip}
				>
					<StyledDropdownItem
						elemBefore={
							value.hasVoted ? (
								<LikeIcon label="" size="medium" />
							) : (
								<LikeIcon
									label=""
									size="medium"
									primaryColor={token('color.icon.selected', B400)}
								/>
							)
						}
						onClick={toggleVote}
						isDisabled={isDisabled}
						{...itemAriaLabel}
					>
						{buttonLabel}
					</StyledDropdownItem>
				</Tooltip>
			</div>
		);
	}

	return (
		<VoteButton
			tooltip={isDisabled ? disabledTooltip : buttonLabel}
			onClick={toggleVote}
			ariaLabel={isDisabled ? disabledTooltip : ariaLabel}
			isDisabled={isDisabled}
			{...(fg('relay-migration-issue-header-and-parent')
				? { useVoteField: useVoteFieldFromProps, ...rest }
				: {})}
		/>
	);
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { Props } from './types';
export default VoteToggle;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-unsafe-style-overrides
const StyledDropdownItem = styled(DropdownItem)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&&': {
		paddingTop: token('space.100', '8px'),
		paddingRight: token('space.150', '12px'),
		paddingBottom: '7px',
		paddingLeft: token('space.150', '12px'),
	},
});
